"use client";

import React from "react";
import { Check, ShieldCheck, Flame } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const pricingTiers = [{
  name: "Project Delivery",
  price: "Milestone-Based",
  description: "Fixed-price delivery for specific project outcomes and deliverables.",
  features: ["Senior PM Leadership", "Milestone Payment Model", "Risk Management", "Outcome Guarantee", "No Monthly Retainers"],
  cta: "Start Project",
  highlight: false,
  icon: ShieldCheck
}, {
  name: "Programme Governance",
  price: "Value-Driven",
  description: "Strategic oversight for complex, multi-project enterprise environments.",
  features: ["Programme Governance", "Resource Optimization", "ServiceNow Integration", "Monthly Value Reviews", "Priority Support"],
  cta: "Scale Delivery",
  highlight: true,
  icon: Flame
}, {
  name: "Specialized Consulting",
  price: "Custom Scope",
  description: "Tailored ServiceNow migrations and technical field services.",
  features: ["ServiceNow Expertise", "Network Surveys", "Technical Credibility", "Zero Surprises", "Bespoke Roadmap"],
  cta: "Get a Quote",
  highlight: false,
  icon: Check
}];
export default function Pricing() {
  return <section id="pricing" className="py-16 bg-[#0d1b2a] bg-linear-to-b from-[#060f1a] to-[#0d1b2a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="font-bold tracking-tight text-white mb-2 !text-5xl">Transparent, Milestone-Based Pricing</h2>
          <p className="text-white/70 text-lg">
            No retainers. No hidden fees. At Projectora, you pay for results delivered, not hours billed.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pricingTiers.map((tier, i) => <Card key={tier.name} data-index={i} className="text-card-foreground rounded-xl relative flex flex-col py-4 gap-4 border-border/40 transition-all !bg-[#112234] border-t-2 border-[#14b8a6] !shadow-none">
              {tier.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge variant="default" className="!bg-[#14b8a6] !text-white px-3 py-1 text-[10px] uppercase tracking-wider font-bold border-none">
                    Most Popular
                  </Badge>
                </div>}
              
              <CardHeader className="px-4 pb-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="p-2 rounded-lg bg-[#14b8a6]/10 text-[#14b8a6]">
                    <tier.icon className="size-5" />
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-white">{tier.name}</CardTitle>
                <CardDescription className="text-sm leading-tight min-h-[40px] text-white/70">
                  {tier.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="px-4 flex-grow">
                <div className="mb-4">
                  <span className="text-2xl font-bold text-white">{tier.price}</span>
                </div>
                <Separator className="mb-4 bg-[#14b8a6]/30" />
                <ul className="space-y-2.5">
                  {tier.features.map((feature, featureIdx) => <li key={featureIdx} className="flex items-start gap-2 text-sm text-white/70">
                      <Check className="size-4 text-[#14b8a6] mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>)}
                </ul>
              </CardContent>

              <CardFooter className="px-4 pt-2">
                <Button asChild variant={tier.highlight ? "default" : "outline"} className="w-full font-semibold">
                  <Link to="/ContactPage" className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm transition-all h-9 px-4 py-2 w-full font-semibold border ${tier.highlight ? "!bg-[#14b8a6] !text-white border-[#14b8a6] hover:!bg-[#14b8a6]/90" : "!bg-transparent !border-[#14b8a6] !text-[#14b8a6] hover:!bg-[#14b8a6]/10"}`}>
                    {tier.cta}
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-white/50 italic">
            * All engagements are governed by our outcome-based delivery model. Custom enterprise pricing available on request.
          </p>
        </div>
      </div>
    </section>;
}